/* eslint-disable no-nested-ternary */
/* eslint-disable max-len */
import type React from 'react';
import {
  useEffect,
  useState,
  useRef,
  useReducer,
  useMemo,
} from 'react';
import InteractionsList from '@components/RunOfShow/InteractionsList';
import { useStreamData } from '@lib/hooks/useStreamData';
import router from 'next/router';
import { getOnFeature } from '@services/feature-service';
import {
  getUserDetails,
  stopEngagement,
  getFeatureByQId,
  updateLiveStream,
  updateLivestreamSession,
} from '@services/youtube-platform.service';
import { motion, AnimatePresence } from 'framer-motion';
import useFullScreen from '@lib/hooks/useFullScreen';
import { useTeamsData } from '@lib/hooks/useTeamsData';
import { useBrandLogo, useIsLogoRequired } from '@store/teamsStore';
import useFirestoreSync from '@lib/hooks/useFirestoreSync';

import { pushDataLayerForEvent } from '@lib/gtag';
import CommentOverlay from '@molecules/CommentOverlay';
import NextPrevCapsule from '@components/RunOfShow/NextPrevCapsule';

import { getAuth, isAuthenticated } from '@services/identity.service';
import WheelEdit from '@components/Features/WinnerWheel/WheelEdit';
import useErrorListener from '@lib/hooks/useErrorListener';
import useStreamParticipantsStatus from '@lib/hooks/useStreamParticipantsStatus';
import useStreamTimeStatus from '@lib/hooks/useStreamTimeStatus';
import {
  getInteractionSettings,
  getMaxCommenters,
  interactionsName,
  platformName,
} from '@services/utils.service';
import { resetZoomGlobals } from '@lib/zoom';
import TimerOverlay from '@components/Timer/TimerOverlay';
import TrackInteractions from '@components/RunOfShow/TrackInteractions';
import { useIpaEventModalControl } from '@lib/hooks/interactionLayout/useIpaEventModal';
import InteractionTitle from '../RunOfShow/InteractionTitle';
import ModerationTabState from '../moderationTabState/reducer';
import Button from '@atoms/new/button';
import ActionToolbar from '@components/ActionToolbar';
import Notification from '@components/Notification';
import ParticipantsLimit from '@components/ParticipantsLimit';
import Toast from '@atoms/Toast';
import { getDefaultTitle } from '@components/Presentation/helpers';
import {
  compareAspectRatioWith16x9or4x3,
  computeLuminance,
  getInteractionBgColorKey,
  interactionColorMap,
} from '@lib/interactionHelper';
import { getLiveLayoutColors } from '@components/RunOfShow/helper/presentationLive';
import { nonInteractiveFeatures } from '@components/Layout/helper';
import useWindowResizeListener from '@lib/hooks/useWindowResizeListener';
import useGetImageProperties from '@components/Presentation/hooks/useGetImageProperties';
import ResetPopup from '@components/ResetPopup';
import ShowAnswerButton from '@components/showAnswer';
import PresentationView from '@components/Layout/PresentationView';
import Tooltip from '@atoms/new/tooltip';
import PinnedTiles from '@components/Features/talkingTiles/pinnedComment';
import { useGeneratedResponses } from './useGeneratedResponses';
import { useHotspot } from '@lib/hooks/useHotSpot';
import { useMutation } from '@tanstack/react-query';
import { createImage } from '@services/generative-ai';
import { useDisclosure } from '@heroui/react';
import GeneratedImage from '@components/Features/WonderWords/GeneratedImage';
import { SummaryModal } from '@components/Features/SummaryModal';
import { interactionsMap } from '@molecules/InteractionTooltip';
import RollingCounter from '@components/RollingCounter';
import Volume from '@components/RunOfShow/volume';
import { getTransitionVariant } from '@components/Presentation/helpers/transitionVariants';
import { SecondaryWorkFlowContent } from '@components/Layout/secondaryWorkFlowContent';
import SuggestedInteractionsDropdown from '@components/Layout/SuggestedInteractionsDropdown';
import {
  CaretDownIcon as CaretDown,
  CaretUpIcon as CaretUp,
  SparkleIcon as Sparkle,
  PlusIcon as AddIcon,
  ArrowsClockwiseIcon as AutorenewRoundedIcon,
  XIcon as CloseIcon,
  FolderIcon as FolderOutlinedIcon,
  ImageIcon,
  LightbulbIcon as LightbulbOutlinedIcon,
  PlayIcon as PlayArrowRoundedIcon,
  GearIcon as SettingsOutlinedIcon,
  ShareIcon as ShareRoundedIcon
} from '@phosphor-icons/react';

interface InteractionLayoutProps {
  interactionType: {
    isResult: boolean;
    isRos: boolean;
  };
  streamId: string;
  colorData: any;
  data: any;
  platformType: string;
  totalQuestions: number;
  streamSetting: any;
  streamName: string;
  rosIndex: number;
  questionIds: string[];
  tempQuestions: any[];
  linkTemp: any;
  defaultThemeSettings: any;
  aiResponsesEnabled?: boolean;
  onClose?: () => void;
  isFullScreenMode?: boolean;
  template?: any;
  onIndexChange?: (idx: number) => void;
}

const InteractionLayout = ({
  interactionType,
  streamId,
  colorData,
  data,
  platformType,
  totalQuestions,
  streamSetting,
  streamName,
  rosIndex,
  questionIds,
  tempQuestions,
  linkTemp,
  defaultThemeSettings = {},
  aiResponsesEnabled = false,
  onClose = () => { },
  isFullScreenMode = false,
  template,
  onIndexChange
}: InteractionLayoutProps) => {
  const [linkQuestions, setLinkQuestions] = useState(tempQuestions);
  const pinnedCommentRef = useRef(null);

  const { isResult, isRos } = interactionType;
  const [isFullScreen, toggle, exitFullscreen] = useFullScreen();
  const [splitDiv, setSplitDiv] = useState(false);
  const featureRef = useRef(null);

  const getCurrentIndex = () => {
    if (tempQuestions.length === 0) return -1;
    // rosIndex can arrive as a string (URL query) or out of bounds — coerce
    // and clamp, otherwise `index + 1` concatenates ("0" + 1 === "01") and
    // slide lookups return undefined.
    const index = Number(rosIndex) || 0;
    return Math.min(Math.max(index, 0), tempQuestions.length - 1);
  };

  const [currentIndex, _setCurrentIndex] = useState(getCurrentIndex());
  const currentIndexRef = useRef(getCurrentIndex());
  const setCurrentIndex = (index: number) => {
    currentIndexRef.current = index;
    _setCurrentIndex(index);
    onIndexChange?.(index);
  };

  const getCurrentRosFeature = () => tempQuestions[currentIndexRef.current];
  const [currentRosFeature, setCurrentRosFeature] = useState(
    getCurrentRosFeature()
  );

  const [currentRosResult, setCurrentRosResult] = useState([]);
  const [impromptuModel, setImpromptuModel] = useState(false);
  const [showRosModel, setShowRosModel] = useState(false);
  const [featureType, setFeatureType] = useState('');
  const {
    featureQuestion,
    setFeatureQuestion,
    setHiddenData,
    sessionHistory,
    setSessionHistory,
  } = useStreamData(s => ({
    featureQuestion: s.featureQuestion,
    setFeatureQuestion: s.setFeatureQuestion,
    setHiddenData: s.setHiddenData,
    sessionHistory: s.sessionHistory,
    setSessionHistory: s.setSessionHistory
  }));

  const [currentFeatureId, setCurrentFeatureId] = useState();
  const [isImpromptu, setIsImpromtu] = useState(false);
  const [comments, dispatch] = useReducer(ModerationTabState, []);
  const [completedQuestions, setCompletedQuestions] = useState(questionIds);

  const [interactionState, _setInteractionState] = useState('stopped');
  const interactionStateRef = useRef(interactionState);
  const setInteractionState = (state: string) => {
    interactionStateRef.current = state;
    _setInteractionState(state);
  };
  const [startInteraction, setStartInteraction] = useState(false);
  const [isStarted, setIsStarted] = useState(false);

  const [defaultSetting, setDefaultSetting] = useState();
  const { teamContext, currentTeam, user } = useTeamsData((s) => ({ teamContext: s.teamContext, currentTeam: s.currentTeam, user: s.user }));
  const isLogoRequired = useIsLogoRequired();
  const brandLogo = useBrandLogo('/images/logos/brand-logo.png');

  const [isLinkRosModal, setIsLinkRosModal] = useState(false);
  const authToken = getAuth();
  const [showTimer, setShowTimer] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [isTrack, setIsTrack] = useState(false);
  const [showMenu, setShowMenu] = useState(true);
  const [openModal, setOpenModal] = useState(
    tempQuestions.length ? 'showMenu' : 'default'
  );
  const [maxParticipants, setMaxParticipants] = useState(0);
  const [displayStreamOverlayPopup] = useState(
    streamSetting?.showOverlay ?? true
  );
  const [showNotification, setShowNotification] = useState(
    streamSetting?.showOverlay ?? true
  );
  const [dismissedIds, setDismissedIds] = useState<any[]>([]);
  const [showResetPopup, setShowResetPopup] = useState(false);

  const [startTime, setStartTime] = useState(Date.now());
  const [pausedInteractions, setPausedInteractions] = useState(new Map());
  const [showAnswerMap, setShowAnswerMap] = useState({});

  const [showMapStylePopup, setShowMapStylePopup] = useState(false);
  const [showTTStylePopup, setShowTTStylePopup] = useState(false);
  const [, _setModerationMode] = useState(false);
  const moderationModeRef = useRef(false);
  const setModerationMode = (value: boolean) => {
    _setModerationMode(value);
    moderationModeRef.current = value;
  };

  const {
    hasReachedMaxParticipants, noOfParticipants, linkLibraryCount, quickQuestionsCount,
    markLinkLibraryAsViewed, markQuickQuestionsAsViewed, numOfParticipants, markNumOfParticipantsAsViewed
  } = useStreamParticipantsStatus(streamId);
  const { hasReachedMaxTimeLimit, monthlyDurationInMins } =
    useStreamTimeStatus(streamId);
  const [showTotalResponders] = useState(streamSetting?.showTotalResponders);
  const [totalResponders, setTotalResponders] = useState(0);
  const [isMuted, setIsMuted] = useState(true);
  const [secondaryWorkflowOpen, setSecondaryWorkflowOpen] = useState(false);
  const [openTrackModal, setOpenTrackModal] = useState('');

  const nodeRef = useRef<HTMLDivElement>(null);
  const prevRef = useRef<HTMLButtonElement>(null);
  const nextRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!currentTeam || !currentTeam.billing_account) return;

    setMaxParticipants(getMaxCommenters(currentTeam, platformType));
  }, [currentTeam]);

  // cleanup old livestream notifications
  useEffect(() => {
    // clear localstorage other than the current streamId
    const allKeys = Object.keys(localStorage);
    const keysToRemove = allKeys
      .filter((key) => key.endsWith('-visible'))
      .filter((key) => !key.startsWith(streamId));

    keysToRemove.forEach((key) => {
      localStorage.removeItem(key);
    });
  }, []);

  const stopFeature = async (syncToFS = false) => {
    setShowImage(false);
    const sessionId = localStorage.getItem('sessionId');
    if (
      currentFeatureId &&
      currentRosFeature?.type &&
      moderationModeRef.current === false
    ) {
      if (!nonInteractiveFeatures.includes(currentRosFeature?.type)) {
        await stopEngagement(streamId, currentRosFeature?.type, {
          engagementId: currentFeatureId,
          platformType,
          sessionId,
        });
      } else {
        updateLivestreamSession(sessionId, {
          engageType: currentRosFeature?.type,
        });
      }
    }

    if (
      isSpinners(currentRosFeature?.type) ||
      isPDFSlide(currentRosFeature?.type)
    ) {
      setShowToast(false);
    } else {
      setShowToast(true);
    }

    if (isSpinners(currentRosFeature?.type)) {
      setInteractionState('stopped');
      setStartInteraction(false);
      setIsStarted(false);
    }

    setIsImpromtu(false);
    setSplitDiv(false);
  };

  const handleStopFeature = () => {
    stopFeature();
    setInteractionState('stopped');
    setStartInteraction(false);
    setIsStarted(false);
  };

  const updateRosCurrentIndex = async (index: number) => {
    await updateLiveStream(streamId, {
      rosIndex: Number(index),
      type: 'rosIndex',
    });
  };

  const start = () => {
    isSpinners(currentRosFeature?.type)
      ? (setShowNotification(false), setSplitDiv(false), setHiddenData({}))
      : setShowNotification(true);

    setShowResetPopup(false);
    setShowToast(false);
    setCurrentRosResult([]);
    setInteractionState('running');
    setStartInteraction(true);
    setIsStarted(true);
  };

  const showMenus = () => {
    setShowMenu(true);
  };

  const hideMenus = () => {
    setShowMenu(false);
    setSecondaryWorkflowOpen(false);
  };

  const isTrackInteractions = (type: string) =>
    ['Quick Questions', 'linkLibrary', 'textTrack', 'fantasticFans', 'credits'].includes(
      type
    );
  const isSpinners = (type: string) => ['wheel', 'newWheel'].includes(type);

  const showCorrectAnswerButton = ['multipleChoice'].includes(
    currentRosFeature?.type
  );

  const isPDFSlide = (type: string) =>
    ['PDF Slide', 'blankScreen'].includes(type);

  const isDurationMoreThanFiveMinutes = ({
    startTime,
    endTime,
  }: {
    startTime: number;
    endTime: number;
  }) => {
    return endTime - startTime > 5 * 60 * 1000; // 5 minutes
  };

  const getFeatureData = async (feature: any, isPlay = false) => {
    if (feature && feature.type) {
      if (isTrackInteractions(feature.type) || isPDFSlide(feature.type)) {
        setCurrentRosFeature(feature);
        setCurrentRosResult([]);
        setInteractionState('stopped');
        setStartInteraction(false);
        setIsStarted(false);
        setShowNotification(true);
        setShowResetPopup(false);
        setShowToast(false);
        return;
      }

      const response = await getFeatureByQId(
        feature.id,
        streamId,
        feature.type
      );

      if (response.status) {
        let currentState;

        if (isPlay || response.entity.length === 0) {
          currentState = 'running';
          pausedInteractions.delete(feature.id);
        } else if (pausedInteractions.has(feature.id)) {
          setPausedInteractions((prev) => new Map(prev));
          currentState = 'stopped';
        } else {
          currentState = isDurationMoreThanFiveMinutes({
            startTime,
            endTime: Date.now(),
          })
            ? 'stopped'
            : 'running';
        }

        setCurrentRosFeature(feature);
        setCurrentRosResult(response.entity);
        setInteractionState(currentState);
        setStartInteraction(currentState === 'running');
        setIsStarted(currentState === 'running');

        const hasComments = response?.entity[0]?.comments;
        const shouldShowNotification = hasComments
          ? !hasComments[0]?.totalWeight
          : !hasComments?.length;

        setShowNotification(shouldShowNotification);
        setShowToast(currentState === 'stopped');
      }
    }
  };

  const updateQuestionsCompleted = async (featureId: string) => {
    if (completedQuestions) {
      const isIdPresent = completedQuestions.find((id) => featureId === id);
      if (!isIdPresent) {
        updateLiveStream(streamId, {
          completedQuestions: {
            questionIds: [...completedQuestions, featureId],
          },
          type: 'completedQuestions',
        });
      }
      setCompletedQuestions([...completedQuestions, featureId]);
    }
  };

  const bgColorKey = useMemo(
    () => getInteractionBgColorKey(currentRosFeature?.type),
    [currentRosFeature?.type]
  );
  const bgColor = useMemo(
    () => (bgColorKey && currentRosFeature?.color?.[bgColorKey]) || '#000000',
    [bgColorKey, currentRosFeature?.color]
  );

  const { isBgLight } = useMemo(() => computeLuminance(bgColor), [bgColor]);

  const colors = useMemo(
    () => getLiveLayoutColors(isBgLight ? 'light' : 'dark'),
    [isBgLight]
  );

  // get background image settings
  const { getImageProperties } = useGetImageProperties(defaultThemeSettings);
  const { src: bgImgSrc, opacity: bgImgOpacity = 100 } = useMemo(
    () => getImageProperties(currentRosFeature?.type, currentRosFeature?.setting),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentRosFeature?.type, currentRosFeature?.setting]
  );

  const allInteractions = [
    'pollWithOptions',
    'wordCloud',
    'freeForm',
    'map',
    'emojisEverywhere',
    'Quick Questions',
    'linkLibrary',
    'ratingPolls',
    'wheel',
    'newWheel',
    'transientThought',
    'talkingTiles',
    'textTrack',
    'fantasticFans',
    'credits',
    'PDF Slide',
    'blankScreen',
  ];

  const handleLinkFeature = (feature: any, index: number) => {
    stopFeature();
    setShowResetPopup(false);
    setIsTrack(false);
    setIsImpromtu(false);
    if (feature) {
      setCurrentRosResult([]);
      getFeatureData(feature);
      setCurrentIndex(index);
      setSplitDiv(false);
    }

    if (isRos && linkQuestions && typeof index === 'number') {
      if (
        allInteractions.includes(linkQuestions[index].type) &&
        !linkQuestions[index]?.isAdhoc
      ) {
        updateRosCurrentIndex(index);
      }
      updateQuestionsCompleted(feature.id);
    }
  };

  const isStartable = () =>
    currentRosFeature &&
    ![
      'Quick Questions',
      'PDF Slide',
      'linkLibrary',
      'textTrack',
      'fantasticFans',
      'credits',
      'blankScreen',
    ].includes(currentRosFeature?.type);

  const featureNextState = () => {
    setCurrentRosResult([]);
    setShowToast(false);
    setShowResetPopup(false);
    setIsTrack(false);

    const index = currentIndexRef.current;

    if (
      (currentRosFeature?.type === 'newWheel' ||
        currentRosFeature?.type === 'wheel') &&
      interactionState === 'running' &&
      currentRosResult?.length === 0
    ) {
      setInteractionState('start');
      return;
    }
    const nextFeature = linkQuestions[index + 1];
    if (nextFeature) {
      stopFeature();
      getFeatureData(JSON.parse(JSON.stringify(nextFeature)));
      setCurrentIndex(index + 1);

      if (isRos) {
        updateQuestionsCompleted(nextFeature.id);

        if (
          allInteractions.includes(nextFeature.type) &&
          !linkQuestions[index]?.isAdhoc
        ) {
          updateRosCurrentIndex(index + 1);
        }
      }
      setSplitDiv(false);
    } else if (
      index === linkQuestions.length - 1 &&
      interactionState === 'running' &&
      isStartable()
    ) {
      // if it's the last interaction, let's stop it.
      handleStopFeature();
    }
  };

  const featurePrevState = () => {
    setCurrentRosResult([]);
    setShowToast(false);
    setShowResetPopup(false);
    setIsTrack(false);
    const index = currentIndexRef.current;

    if (index < 1) {
      return;
    }

    const prevFeature = linkQuestions[index - 1];
    if (prevFeature) {
      stopFeature();
      getFeatureData(JSON.parse(JSON.stringify(prevFeature)));
      setCurrentIndex(index - 1);

      if (isRos) {
        updateQuestionsCompleted(prevFeature.id);

        if (
          allInteractions.includes(prevFeature.type) &&
          !linkQuestions[index]?.isAdhoc
        ) {
          updateRosCurrentIndex(index - 1);
        }
      }
      setSplitDiv(false);
    }
  };

  const pushToGa = (featureName = '', tempInteractionState = '') => {
    pushDataLayerForEvent(
      `feature_${featureName}_state_${tempInteractionState}`,
      { platform: platformType }
    );
  };

  useEffect(() => {
    pushToGa(currentRosFeature?.type, interactionState);
  }, [interactionState]);

  // Sync currentRosFeature with current index for UI updates
  useEffect(() => {
    if (linkQuestions.length > 0 && currentIndex >= 0 && currentIndex < linkQuestions.length) {
      const feature = linkQuestions[currentIndex];
      if (feature) {
        setCurrentRosFeature(feature);
      }
    }
  }, [currentIndex, linkQuestions]);

  const handleQuickQuestions = () => {
    stopFeature();
    setIsTrack(true);
    setInteractionState('stopped');
    getFeatureData({
      title: getDefaultTitle('Quick Questions'),
      type: 'Quick Questions',
      setting: getInteractionSettings('Quick Questions', streamSetting),
      color: colorData,
    });
  };

  const handleLinkLibrary = () => {
    stopFeature();
    setIsTrack(true);
    setInteractionState('stopped');
    getFeatureData({
      title: getDefaultTitle('linkLibrary'),
      type: 'linkLibrary',
      setting: getInteractionSettings('linkLibrary', streamSetting),
      color: colorData,
    });
  };

  const handleTextTrack = () => {
    stopFeature();
    setIsTrack(true);
    setInteractionState('stopped');
    getFeatureData({
      title: getDefaultTitle('textTrack'),
      type: 'textTrack',
      setting: getInteractionSettings('textTrack', streamSetting),
      color: colorData,
    });
  };

  const handleFantasticFans = () => {
    stopFeature();
    setIsTrack(true);
    setInteractionState('stopped');
    getFeatureData({
      title: getDefaultTitle('fantasticFans'),
      type: 'fantasticFans',
      setting: getInteractionSettings('fantasticFans', streamSetting),
      color: colorData,
    });
  };

  const handleCredits = () => {
    stopFeature();
    setIsTrack(true);
    setInteractionState('stopped');
    getFeatureData({
      title: getDefaultTitle('credits'),
      type: 'credits',
      setting: getInteractionSettings('credits', streamSetting),
      color: colorData,
    });
  };

  const handleFullScreen = () => {
    toggle();
  };

  const handleStreamError = (error: any, docId: string) => {
    resetZoomGlobals();
    setStartInteraction(false);
    localStorage.setItem(
      'streamError',
      JSON.stringify({ ...error, docId, liveStreamId: streamId })
    );
  };

  useErrorListener(isResult, streamId, handleStreamError);

  const toggleShowIpaEventJoinDetailsModal = (
    flag: boolean,
    syncToFS = false
  ) => { };
  const { isPresented, addToInitiazedStreams } = useIpaEventModalControl(
    toggleShowIpaEventJoinDetailsModal,
    streamId,
    linkQuestions,
    platformType
  );

  const handleUnlinkRos = () => {
    setLinkQuestions([]);
    setCurrentRosFeature(null);
    const { userDetailsId } = router.query;
    router.replace({
      pathname: `/streams/${streamId}/ros`,
      query: userDetailsId ? { userDetailsId } : undefined,
    });
  };

  const handleLinkRos = (title: string, interactions: any[]) => {
    setLinkQuestions(interactions);
    getFeatureData(interactions[0]);
    setCurrentIndex(0);
    const { userDetailsId } = router.query;
    router.replace({
      pathname: `/streams/${streamId}/ros`,
      query: userDetailsId ? { userDetailsId } : undefined,
    });
    if (platformType === 'ipa' && !isPresented) {
      addToInitiazedStreams();
    }
    setIsLinkRosModal(false);
    if (openModal === 'default') {
      setOpenModal('showMenu');
    }
  };

  const handleKeyStrokes = (e: KeyboardEvent) => {
    if (impromptuModel || showRosModel || isLinkRosModal) {
      return;
    }

    const { key } = e;

    if (key.match(/(ArrowRight|ArrowDown)/)) {
      e.preventDefault();

      if (!nextRef?.current) return;
      nextRef.current.click();
      return;
    }

    if (key.match(/(ArrowLeft|ArrowUp)/)) {
      e.preventDefault();

      if (
        currentIndexRef.current === 0 &&
        platformType.match(/(ipa|other-online-events|hybrid)/)
      ) {
        toggleShowIpaEventJoinDetailsModal(true, true);
        return;
      }

      if (!prevRef?.current) return;
      prevRef.current.click();
      return;
    }

    if (key.match(/ /) && isStartable()) {
      e.preventDefault();
      if (
        interactionStateRef.current === 'running' ||
        interactionStateRef.current === 'stopped'
      ) {
        handleStopIcon();
        return;
      }
    }

    if (key.match(/Enter/) && isStartable()) {
      e.preventDefault();
      if (
        interactionStateRef.current === 'running' ||
        interactionStateRef.current === 'stopped'
      ) {
        handleStopIcon();
      }
    }
  };

  const syncConfig = {
    start,
    stopFeature,
    featureNextState,
    featurePrevState,
    handleLinkFeature,
    toggleShowIpaEventJoinDetailsModal,
    streamId,
    authToken,
    moderationModeRef,
    currentIndexRef,
    currentRosFeature,
    setModerationMode,
    setCurrentRosFeature,
    setCurrentIndex,
    handleQuickQuestions,
    handleLinkLibrary,
    handleTextTrack,
    handleFantasticFans,
    handleFullScreen,
    setFeatureQuestion,
    handleUnlinkRos,
    handleLinkRos,
    disconnect: () => { },
    handleActiveInteraction: () => { },
  };

  const syncHandler = useFirestoreSync(syncConfig);

  const { activeInteractionRef, meta, updateFSMeta, pushToFS } = syncHandler;

  useEffect(() => {
    if (!teamContext?.oid) return;

    const _setDefaultSetting = async () => {
      const userId = teamContext?.oid;
      const userDetails = await getUserDetails(userId);
      if (userDetails.status) {
        setDefaultSetting({ ...userDetails.entity.settings, ...streamSetting });
      }
    };

    _setDefaultSetting();
  }, [teamContext]);

  const handleStartImpromptuFeature = () => {
    setCurrentRosFeature(featureQuestion);
    const currentState = isTrackInteractions(featureQuestion.type)
      ? 'stopped'
      : 'running';
    setInteractionState(currentState);
    setStartInteraction(currentState === 'running');
    setIsStarted(currentState === 'running');
    isSpinners(featureQuestion.type)
      ? setShowNotification(false)
      : setShowNotification(true);
    setShowResetPopup(false);
    setShowToast(false);
    setFeatureQuestion({ id: '', type: '', title: '', options: [] });
    setCurrentRosResult([]);
  };

  const handleStartRosFeature = (feature: any) => {
    setIsImpromtu(false);
    setIsTrack(false);
    getFeatureData(feature);
    updateQuestionsCompleted(feature.id);
  };

  useEffect(() => {
    if (featureQuestion && featureQuestion.type) {
      handleStartImpromptuFeature();
    } else if (currentRosFeature && currentRosFeature?.autoStart === false) {
      handleStartRosFeature(currentRosFeature);
    }
    if (rosIndex === null) updateRosCurrentIndex(0);
  }, [featureQuestion]);

  useEffect(() => {
    window.removeEventListener('keydown', handleKeyStrokes);
    window.addEventListener('keydown', handleKeyStrokes);
    return () => {
      window.removeEventListener('keydown', handleKeyStrokes);
    };
  }, [
    currentIndexRef.current,
    currentRosFeature,
    isStarted,
    interactionState,
    impromptuModel,
    showRosModel,
  ]);

  useEffect(() => {
    if (linkQuestions.length) {
      setIsImpromtu(false);
    }
  }, [linkQuestions]);

  useEffect(() => {
    if (currentRosFeature?.type !== 'wheel' && interactionState === 'stopped') {
      // @ts-expect-error dispatch is not a function
      dispatch({ type: 'UPDATE_TAB', data: [] });
    }
  }, [interactionState, currentRosFeature]);
  const { isHotspotEnabled, handleHotspotClose } = useHotspot(
    'sandbox:next/prev:button:hotspot'
  );

  useEffect(() => {
    if (!Object.keys) return;
    if (
      currentRosFeature?.type === 'PDF Slide' &&
      !sessionHistory[`slide-${currentRosFeature?.id}`] &&
      currentRosFeature.id !== undefined
    ) {
      setSessionHistory({
        ...sessionHistory,
        [`slide-${currentRosFeature.id}`]: {
          createdTime: new Date(),
          rosId: currentRosFeature?.id,
          type: currentRosFeature?.type,
          key: currentRosFeature?.presentation_slide_s3_key,
        },
      });
    }
  }, [currentRosFeature?.id, sessionHistory]);

  useEffect(() => {
    if (!currentRosFeature) return;

    if (!currentRosFeature.autoStart) return;

    start();
  }, [currentRosFeature]);

  const handleMouseMove = (event: React.MouseEvent) => {
    const div = nodeRef.current;
    if (!div) return;
    const rect = div.getBoundingClientRect();
    const isAtBottom = rect.bottom - event.clientY <= 40;
    if (isAtBottom) showMenus();
    else hideMenus();
  };

  useEffect(
    () => () => {
      exitFullscreen();
    },
    []
  );

  const handleReplayIcon = () => {
    setShowToast(false);
    setShowResetPopup(true);
  };

  const handlePlayIcon = async () => {
    setShowImage(false);
    setShowResetPopup(false);
    setStartTime(Date.now());
    await getFeatureData(currentRosFeature, true);
    if (
      currentRosFeature?.type === 'wheel' ||
      currentRosFeature?.type === 'newWheel'
    ) {
      handleResetResults();
      setInteractionState('start');
    }
  };

  const handlePauseIcon = () => {
    setPausedInteractions((prev) =>
      new Map(prev).set(currentRosFeature?.id, true)
    );
    setShowResetPopup(false);
    setShowNotification(false);
    handleStopFeature();
  };

  const handleStopIcon = () => {
    if (interactionState === 'running') {
      handleStopFeature();
    }
    setShowNotification(false);
    handleStopFeature();
  };

  useEffect(() => {
    handleResetResults();
  }, [aiResponsesEnabled]);

  const handleResetResults = async () => {
    if (interactionState === 'running') {
      setInteractionState('stopped');
      setStartInteraction(false);
      setIsStarted(false);
      await stopFeature();
    }
    start();
  };

  const [setting] = useState(
    getInteractionSettings(featureType, streamSetting)
  );
  const [color] = useState(colorData);

  const shouldShowSecondaryWorkflow = () => {
    return ![
      'wheel',
      'newWheel',
      'textTrack',
      'fantasticFans',
      'credits',
      'linkLibrary',
      'Quick Questions',
      'PDF Slide',
    ].includes(currentRosFeature?.type);
  };

  const handleCloseNotification = () => {
    setDismissedIds((prev) => [...prev, currentRosFeature?.id]);
    setShowNotification(false);
  };

  const windowSize = useWindowResizeListener();

  const screenBasedClass = compareAspectRatioWith16x9or4x3(
    windowSize,
    linkTemp.layoutMode
  )
    ? 'h-full mx-auto'
    : 'w-full my-auto';

  useGeneratedResponses({
    feature: currentRosFeature,
    streamId,
    streamTitle: template?.title || '',
    enabled: aiResponsesEnabled || interactionState === 'stopped',
  });

  const [isCopied, setIsCopied] = useState(false);

  const sandboxUrl = `${process.env.NEXT_PUBLIC_SANDBOX_URL}/ros-preview-alpha${window.location.search}`;

  const handleShare = () => {
    if (isCopied) return;
    navigator.clipboard.writeText(sandboxUrl);
    setIsCopied(true);
    setTimeout(() => {
      setIsCopied(false);
    }, 2000);
  };
  const [isRosPreviewAlpha, setIsRosPreviewAlpha] = useState(false);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      if (window.location.href.includes('ros-preview-alpha')) {
        setIsRosPreviewAlpha(true);
      }
    }
  }, []);

  const [showImage, setShowImage] = useState(false);

  const {
    data: imageData,
    mutateAsync: createImageFn,
    isPending: isCreatingImage,
  } = useMutation({
    mutationFn: createImage,
    onError: (error) => {
      console.error('Error generating image:', error);
    },
  });

  const {
    isOpen: isSummaryOpen,
    onOpen: onSummaryOpen,
    onClose: onSummaryClose,
    onOpenChange: onSummaryOpenChange,
  } = useDisclosure({
    onOpen: () => {
      setShowImage(false);
      handleStopFeature();
    },
    onClose: () => {
      handlePlayIcon();
    },
  });

  // Get transition type from template
  const templateTransitionType = template?.transition_type;

  // Use current slide's transition type if available, otherwise use template transition type or default
  const currentTransitionType = currentRosFeature?.transition_type || templateTransitionType || 'none';
  const variant = useMemo(() => getTransitionVariant(currentTransitionType), [currentTransitionType]);
  const extraStyle = useMemo(() => variant.style || {}, [variant]);

  return (
    <div className={'flex w-full h-full relative'}>
      <div className={'flex w-full h-full relative'} id="presentationView">
        <PresentationView
          targetId="presentationView"
          linkTemp={linkTemp}
          style={{ background: bgColor }}
          className={`flex overflow-hidden relative transition-[border-color,box-shadow] ${!isRosPreviewAlpha && 'border-grey-100 dark:border-grey-800 border-6 rounded-[50px] shadow-[inset_0px_0px_10px_rgba(0,0,0,0.4),inset_0px_0px_10px_rgba(255,255,255,1)] dark:shadow-[inset_0px_0px_14px_rgba(0,0,0,0.6),inset_0px_0px_14px_rgba(255,255,255,0.1)]'} ${screenBasedClass}`}
        >
          <div
            onMouseMove={(event) => {
              const { clientX } = event;
              if (clientX < 50) {
                showMenus();
              } else {
                hideMenus();
              }
            }}
            className="sidebar-overlay absolute z-10 h-3/4"
          ></div>
          <section
            id="sidenav"
            onMouseEnter={() => showMenus()}
            onMouseLeave={() => {
              if (
                (isStartable() && interactionState === 'running') ||
                !isStartable()
              ) {
                hideMenus();
              }
            }}
            className="shrink-0 ml-6 z-20 absolute h-full"
          >
            {
              <>
                <motion.div
                  initial={{ x: -56 }}
                  transition={{ duration: 0.5, delay: showMenu ? 0 : 1 }}
                  animate={{ x: showMenu ? 0 : -90 }}
                  id="leftMenu"
                  className={'flex flex-col h-full w-full max-w-[52px]'}
                >
                  <div className={'h-full flex flex-1 flex-col gap-y-6'}>
                    <div className="flex flex-1"></div>

                    <div className="cursor-pointer">
                      {user?.edu_settings?.is_edu_account && (
                        <div className="relative h-20">
                          <TrackInteractions
                            user={user}
                            linkLibraryCount={linkLibraryCount}
                            setOpenTrackModal={setOpenTrackModal}
                            quickQuestionsCount={quickQuestionsCount}
                            markLinkLibraryAsViewed={markLinkLibraryAsViewed}
                            markQuickQuestionsAsViewed={markQuickQuestionsAsViewed}
                            numOfParticipants={numOfParticipants}
                            markNumOfParticipantsAsViewed={markNumOfParticipantsAsViewed}
                            layoutTheme={isBgLight ? 'light' : 'dark'}
                            {...{
                              handleLinkLibrary,
                              handleQuickQuestions,
                              handleTextTrack,
                              handleFantasticFans,
                              handleCredits,
                            }}
                          />
                        </div>
                      )}

                      {linkQuestions.length !== 0 && (
                        <div id="rosFeature">
                          <span
                            id="rosText"
                            style={{ color: isBgLight ? '#000' : '#FFF' }}
                            className="flex items-center font-semibold text-xs leading-3 text-grey-300 mb-2 justify-center"
                          >
                            Slides
                          </span>

                          <div className="flex">
                            <InteractionsList
                              questionIds={completedQuestions}
                              interactions={linkQuestions}
                              handleLinkFeature={handleLinkFeature}
                              currentRosFeature={currentRosFeature}
                              layoutTheme={isBgLight ? 'light' : 'dark'}
                            />
                          </div>
                        </div>
                      )}

                      {/* {shouldShowSecondaryWorkflow() && (
                        <div className="mt-4">
                          <div className="flex justify-center items-center gap-2 mb-2">
                            <p
                              style={{ color: isBgLight ? '#000' : '#FFF' }}
                              className="font-semibold text-xs leading-3"
                            >
                              Xtend
                            </p>
                            <Tooltip
                              placement="right"
                              content="Create a secondary interaction from your current slide."
                            >
                              <HelpIcon size={20} />
                            </Tooltip>
                          </div>
                          <div
                            className={`${colors.containerColor} flex items-center justify-center gap-2 rounded-full cursor-not-allowed p-0.5`}
                          >
                            <Tooltip
                              placement="right"
                              content="New Winning Wheel: Available on the live presentation page."
                            >
                              <div
                                className={`group flex items-center justify-center rounded-full opacity-50 cursor-not-allowed w-12 h-12 ${colors.hoverBg}`}
                              >
                                <img src="/images/ros/interactions/wheel.svg" />
                              </div>
                            </Tooltip>
                          </div>
                        </div>
                      )} */}
                    </div>

                    <div className="flex flex-1"></div>
                  </div>
                </motion.div>
              </>
            }
          </section>

          <div className="flex w-full">
            <div className="flex w-full relative flex-col">
              <div
                className="flex items-center justify-between absolute left-0 p-6 z-10 top-0 right-0"
                onMouseEnter={() => showMenus()}
                onMouseLeave={() => {
                  if (
                    (isStartable() && interactionState === 'running') ||
                    !isStartable()
                  ) {
                    hideMenus();
                  }
                }}
              >
                <motion.div
                  initial={{ y: -100 }}
                  transition={{ duration: 0.5, delay: showMenu ? 0 : 1 }}
                  animate={{
                    y: showMenu ? 0 : -100,
                  }}
                  id="topMenu"
                  className={'flex justify-between h-full flex-1 '}
                >
                  <div className='flex gap-2 items-center z-20'>
                    <Button
                      className={`${colors.containerColor} ${colors.hoverBg} ${colors.textColor} h-12! rounded-full opacity-50 cursor-not-allowed`}
                      startContent={
                        <AddIcon className={`${colors.textColor}`} width="16" />
                      }
                      tooltip={{
                        content:
                          'New interactions: Available on the live presentation page.',
                        placement: 'bottom',
                      }}
                      kind="custom"
                    >
                      New Interaction
                    </Button>
                    {shouldShowSecondaryWorkflow() &&
                      <div className='ml-4'>
                        <SuggestedInteractionsDropdown
                          onOpenChange={setSecondaryWorkflowOpen}
                          close={secondaryWorkflowOpen}
                          trigger={
                            <Button
                              size='lg'
                              kind='custom'
                              onClick={() => setSecondaryWorkflowOpen(!secondaryWorkflowOpen)}
                              className={`${colors.containerColor} ${colors.hoverBg} ${colors.textColor} z-20 h-12! rounded-full`}
                              startContent={<Sparkle size={16} />}
                              endContent={secondaryWorkflowOpen ? <CaretUp size={16} className={`${colors.textColor}`} /> :
                                <CaretDown size={16} className={`${colors.textColor}`} />}
                            >
                              Create from responses
                            </Button>
                          }
                        >
                          <SecondaryWorkFlowContent
                            colors={colors}
                            isPreview={true}
                            linkTemp={linkTemp}
                            streamId={streamId}
                            setShowImage={setShowImage}
                            createImageFn={createImageFn}
                            onSummaryOpen={onSummaryOpen}
                            currentRosResult={currentRosResult}
                            currentRosFeature={currentRosFeature}
                            handleStopFeature={handleStopFeature}
                            secondaryWorkflowOpen={secondaryWorkflowOpen}
                            setSecondaryWorkflowOpen={setSecondaryWorkflowOpen}
                            createWinningWheelForCurrentInteraction={() => { }}
                          />
                        </SuggestedInteractionsDropdown>
                      </div>
                    }
                  </div>

                  <div className="flex gap-2">
                    {isFullScreenMode && (
                      <Button
                        className="rounded-full h-12"
                        startContent={<ShareRoundedIcon />}
                        onPress={handleShare}
                      >
                        {isCopied ? 'Link copied!' : 'Share link'}
                      </Button>
                    )}
                    <Button
                      className={`${colors.containerColor} ${colors.hoverBg} ${colors.textColor} rounded-full! h-12! w-12! opacity-50 cursor-not-allowed`}
                      isIconOnly={true}
                      tooltip={{
                        content:
                          'My Presentations: Available on the live presentation page.',
                        placement: 'bottom',
                      }}
                      kind="custom"
                    >
                      <FolderOutlinedIcon
                        className={`fill-current ${isBgLight ? 'text-grey-900 group-hover:text-grey-800' : 'text-white group-hover:text-grey-50'} transition-none!`}
                        width="18"
                      />
                    </Button>

                    {linkQuestions.length > 0 && (
                      <Button
                        isIconOnly={true}
                        className={`${colors.containerColor} ${colors.hoverBg} ${colors.textColor} rounded-full! h-12! w-12! opacity-50 cursor-not-allowed`}
                        size="lg"
                        tooltip={{
                          content:
                            'Customize slide: Available on the live presentation page.',
                          placement: 'bottom',
                        }}
                        kind="custom"
                      >
                        <SettingsOutlinedIcon
                          className={`fill-current ${isBgLight ? 'text-grey-900 group-hover:text-grey-800' : 'text-white group-hover:text-grey-50'} transition-none!`}
                          width="18"
                        />
                      </Button>
                    )}
                  </div>
                </motion.div>
              </div>

              <AnimatePresence mode="popLayout" initial={false}>
                <motion.div
                  id="interactionTransition"
                  className='flex flex-col flex-1 h-full'
                  key={currentRosFeature?.id}
                  initial={variant.initial}
                  animate={variant.animate}
                  exit={variant.exit}
                  style={extraStyle}
                  transition={{
                    ...variant.transition,
                    duration: 0.3 // Faster transition for smoother movement
                  }}
                >
                  {bgImgSrc && (
                    ['mp4', 'webm'].includes(bgImgSrc.split('.').pop()?.toLowerCase() || '') ? (
                      <video
                        key={bgImgSrc}
                        style={{ opacity: bgImgOpacity / 100 }}
                        src={`${process.env.NEXT_PUBLIC_BUCKET_URL}${bgImgSrc}`}
                        className="absolute size-full object-cover"
                        autoPlay
                        loop
                        muted
                        playsInline
                        preload="auto"
                        disablePictureInPicture
                        ref={(el) => {
                          if (el && el.paused) {
                            el.play().catch(() => { });
                          }
                        }}
                      />
                    ) : (
                      <img
                        key={bgImgSrc}
                        style={{ opacity: bgImgOpacity / 100 }}
                        src={`${process.env.NEXT_PUBLIC_BUCKET_URL}${bgImgSrc}`}
                        className="absolute size-full object-cover"
                        alt="background image"
                        width={720}
                        height={360}
                      />
                    )
                  )}

                  {!isPDFSlide(currentRosFeature?.type) ? (
                    <div className="w-full flex flex-col px-24">
                      <div className={'pb-6 pt-12 flex flex-col z-2 relative'}>
                        <InteractionTitle
                          title={currentRosFeature?.title}
                          titleFont={currentRosFeature?.setting?.HeadingFont}
                          slideUrl={null}
                          titleColor={
                            currentRosFeature?.color?.[
                              interactionColorMap.heading[
                            currentRosFeature?.type as keyof typeof interactionColorMap.heading
                              ]
                            ]
                          }
                          type={currentRosFeature?.type}
                          isLogoRequired={isLogoRequired}
                          logoUrl={brandLogo}
                          layoutTheme={isBgLight ? 'light' : 'dark'}
                        />
                      </div>
                    </div>
                  ) : (
                    ''
                  )}

                  <section
                    id="hero"
                    className="flex flex-1 gap-x-3 relative overflow-hidden px-24 pb-20"
                  >
                    {displayStreamOverlayPopup &&
                      showNotification &&
                      !dismissedIds.includes(currentRosFeature?.id) &&
                      interactionState !== 'stopped' &&
                      !(
                        isSpinners(currentRosFeature?.type) ||
                        currentRosFeature?.type === 'blankScreen'
                      ) && (
                      <Notification
                        platformName={
                          platformName[platformType as keyof typeof platformName]
                        }
                        handleCloseNotification={handleCloseNotification}
                        layoutMode={linkTemp.layoutMode}
                        currentRosFeature={currentRosFeature}
                      />
                    )}

                    <div
                      className={`w-full rounded-lg  ${interactionState === 'ready' && currentRosFeature && !['Quick Questions', 'PDF Slide', 'textTrack'].includes(currentRosFeature?.type) && 'filter'}`}
                    >
                      <div className="h-full">
                        {/* @ts-expect-error */}
                        <CommentOverlay
                          featureSettings={currentRosFeature?.setting}
                          streamId={streamId}
                          isStopped={isResult}
                        />

                        {showToast &&
                          isRos &&
                          isStartable() &&
                          interactionState === 'stopped' &&
                          !splitDiv &&
                          currentRosFeature?.type !== 'wheel' &&
                          currentRosFeature?.type !== 'newWheel' && (
                          <div className="absolute right-8 bottom-24 z-20">
                            <Toast
                              variant="notification"
                              showIcon={false}
                              header="Interaction is paused"
                              body={
                                isSpinners(currentRosFeature?.type)
                                  ? 'To restart this interaction, click below.'
                                  : 'To resume this interaction, click below.'
                              }
                              onClose={() => setShowToast(false)}
                              children={
                                <Button
                                  kind="secondary"
                                  onPress={handlePlayIcon}
                                  startContent={
                                    <PlayArrowRoundedIcon
                                      className="fill-current text-black dark:text-grey-200"
                                      width="16"
                                    />
                                  }
                                >
                                  {isSpinners(currentRosFeature?.type)
                                    ? 'Restart'
                                    : 'Resume Interaction'}
                                </Button>
                              }
                            />
                          </div>
                        )}

                        {currentRosFeature?.type === 'wheel' && splitDiv && (
                          <WheelEdit />
                        )}

                        <div
                          ref={nodeRef}
                          className="h-full size-full z-2"
                          onMouseMove={handleMouseMove}
                        >
                          {showImage ? (
                            <GeneratedImage
                              error={!imageData?.status ? imageData?.message : null}
                              imageUrl={
                                imageData?.status ? imageData?.entity?.url : null
                              }
                              isPending={isCreatingImage}
                              showImage={true}
                              onClose={handlePlayIcon}
                              onRetry={async () => {
                                setShowImage(true);
                                await createImageFn({
                                  questionId: currentRosFeature?.id,
                                  streamId,
                                  type: currentRosFeature?.type,
                                  layoutMode: linkTemp.layoutMode,
                                  engagementId: currentFeatureId,
                                });
                              }}
                            />
                          ) : getOnFeature({
                            setIsStarted,
                            interactionState,
                            setInteractionState,
                            setStartInteraction,
                            interactionType,
                            currentRosFeature,
                            data,
                            streamId,
                            colorData,
                            platformType,
                            ref: featureRef,
                            totalQuestions,
                            streamSetting,
                            currentRosResult,
                            setCurrentRosResult,
                            startInteraction,
                            setCurrentFeatureId,
                            isImpromptu,
                            splitDiv,
                            setSplitDiv,
                            dispatchTab: dispatch,
                            comments,
                            isFullScreen,
                            linkQuestions,
                            setShowRosModel,
                            stopFeature,
                            cache: {},
                            featurePrevState,
                            setCurrentIndex,
                            setShowWheelModal: () => { },
                            impromptuWheelModalObj: null,
                            setCurrentRosFeature,
                            featureNextState,
                            streamName,
                            setShowToast,
                            moderationModeRef,
                            activeInteractionRef,
                            meta,
                            updateFSMeta,
                            pushToFS,
                            pushCmdToFS: () => { },
                            setShowNotification,
                            setStartTime,
                            showAnswerMap,
                            layoutMode: linkTemp.layoutMode,
                            showInstantCustomisationPopup: false,
                            pinnedCommentRef,
                            setTotalResponders,
                            isMuted,
                            setIsMuted
                          })}
                        </div>
                      </div>
                    </div>

                    {showTimer && (
                      <TimerOverlay onClose={() => setShowTimer(false)} />
                    )}
                  </section>
                </motion.div>
              </AnimatePresence>

              <section
                id="footer"
                onMouseEnter={() => showMenus()}
                onMouseLeave={() => {
                  if (
                    (isStartable() && interactionState === 'running') ||
                    !isStartable()
                  ) {
                    hideMenus();
                  }
                }}
                className="z-20 absolute w-full bottom-0"
              >
                {
                  <div className="flex p-6">
                    {showResetPopup && isStartable() && (
                      <ResetPopup
                        onCancel={() => setShowResetPopup(false)}
                        interactionName={
                          interactionsName[
                            currentRosFeature?.type as keyof typeof interactionsName
                          ]?.name
                        }
                        interactionType={currentRosFeature?.type}
                        setShowResetPopup={setShowResetPopup}
                        handleResetResults={handleResetResults}
                      />
                    )}

                    {!isAuthenticated(authToken) &&
                      currentIndexRef.current === 0 &&
                      !isImpromptu && (
                      <img
                        alt="click here"
                        src="/images/sandbox/click-here.png"
                        className="absolute bottom-[60px] left-[5px] w-32"
                      />
                    )}

                    {(!isImpromptu || isTrack) && !!linkQuestions.length && (
                      <NextPrevCapsule
                        {...{
                          linkQuestions,
                          currentIndex: currentIndexRef.current,
                          featureNextState,
                          featurePrevState,
                          prevRef,
                          nextRef,
                          isTrack,
                          pulseProps: {
                            tooltip: 'Click here to proceed to the next slide',
                            isEnabled:
                              currentIndexRef.current === 0
                                ? isHotspotEnabled
                                : false,
                            onClose: handleHotspotClose,
                          } as any,
                          layoutTheme: isBgLight ? 'light' : 'dark',
                          layoutMode: linkTemp.layoutMode,
                        }}
                      />
                    )}

                    <Volume
                      layoutTheme={isBgLight ? 'light' : 'dark'}
                      layoutMode={linkTemp.layoutMode}
                      isMuted={isMuted}
                      setIsMuted={setIsMuted}
                      currentRosFeature={currentRosFeature}
                    />

                    <motion.div
                      initial={{ y: 95 }}
                      transition={{ duration: 0.5, delay: showMenu ? 0 : 1 }}
                      animate={{
                        y: showMenu ? 0 : 95,
                      }}
                      id="bottomMenu"
                      className="w-full"
                    >
                      <div className="w-full flex items-center justify-between">
                        <div className="w-fit flex items-center">
                          <ActionToolbar
                            isDisabled={false}
                            showPauseIcon={
                              interactionState === 'running' &&
                              !isSpinners(currentRosFeature?.type) &&
                              !isPDFSlide(currentRosFeature?.type) &&
                              !isTrackInteractions(currentRosFeature?.type)
                            }
                            showPlayIcon={
                              interactionState === 'stopped' &&
                              !isSpinners(currentRosFeature?.type) &&
                              !isPDFSlide(currentRosFeature?.type) &&
                              !isTrackInteractions(currentRosFeature?.type)
                            }
                            replayIconText={
                              isSpinners(currentRosFeature?.type)
                                ? `Reset ${interactionsName[currentRosFeature?.type as keyof typeof interactionsName]?.name}`
                                : 'Reset Results'
                            }
                            showReplayIcon={isStartable()}
                            showResetPopup={showResetPopup}
                            handleReplayIcon={handleReplayIcon}
                            handlePlayIcon={handlePlayIcon}
                            handlePauseIcon={handlePauseIcon}
                            setShowTimer={setShowTimer}
                            isFullScreen={isFullScreen}
                            handleFullScreen={handleFullScreen}
                            isZoomApp={false}
                            isPowerpoint={false}
                            showQrCode={false}
                            handleQrCode={() => { }}
                            layoutTheme={isBgLight ? 'light' : 'dark'}
                            showInstructions={false}
                            currentRosFeature={currentRosFeature}
                            setCurrentRosFeature={setCurrentRosFeature}
                            setLinkQuestions={setLinkQuestions}
                            showMapStylePopup={showMapStylePopup}
                            setShowMapStylePopup={setShowMapStylePopup}
                            showTTStylePopup={showTTStylePopup}
                            setShowTTStylePopup={setShowTTStylePopup}
                            layoutMode={linkTemp.layoutMode}
                            showInstantCustomisationPopup={false}
                            onboardingSettings={{
                              SHOW_ONBOARDING_ICON_GUIDE: false,
                            }}
                          />

                          {shouldShowSecondaryWorkflow() && (
                            <div
                              className={`${colors.containerColor} flex items-center justify-center gap-2 rounded-full cursor-pointer ml-4 p-0.5`}
                            >
                              <Tooltip content="Summary" placement="top">
                                <div
                                  className={`${colors.hoverBg} ${colors.textColor} group flex items-center justify-center rounded-full w-12 h-12 ${colors.hoverBg}`}
                                  onClick={onSummaryOpen}
                                >
                                  <LightbulbOutlinedIcon />
                                </div>
                              </Tooltip>

                              <Tooltip
                                content="Turn responses into images"
                                placement="top"
                              >
                                <div
                                  onClick={async () => {
                                    const questionId = currentRosFeature?.id;
                                    handleStopFeature();

                                    setShowImage(true);
                                    await createImageFn({
                                      questionId,
                                      streamId,
                                      type: currentRosFeature?.type,
                                      layoutMode: linkTemp.layoutMode,
                                      engagementId: currentFeatureId,
                                    });
                                  }}
                                  className={`${colors.hoverBg} ${colors.textColor} group flex items-center justify-center rounded-full w-12 h-12`}
                                >
                                  <ImageIcon />
                                </div>
                              </Tooltip>
                            </div>
                          )}
                        </div>

                        <div className="w-fit flex gap-2">
                          <ParticipantsLimit
                            hasReachedMaxParticipants={hasReachedMaxParticipants}
                            noOfParticipants={noOfParticipants}
                            maxParticipants={maxParticipants}
                            text={`${noOfParticipants}/${maxParticipants}`}
                            layoutTheme={isBgLight ? 'light' : 'dark'}
                            linkLibrary={0}
                            quickQuestions={0}
                          />

                          <Tooltip
                            content="Disconnect StreamAlive"
                            placement="top-end"
                          >
                            <Button
                              onPress={onClose}
                              className="h-12! rounded-full! px-8"
                              endContent={<CloseIcon size={20} />}
                            >
                              Exit
                            </Button>
                          </Tooltip>
                        </div>
                      </div>
                    </motion.div>

                    <div className="inline-flex gap-2 absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
                      <Button
                        isDisabled={isCreatingImage}
                        onPress={() => {
                          createImageFn({
                            questionId: currentRosFeature?.id,
                            streamId,
                            type: currentRosFeature?.type,
                            layoutMode: linkTemp.layoutMode,
                            engagementId: currentFeatureId,
                            newImage: true,
                          });
                        }}
                        startContent={<AutorenewRoundedIcon />}
                        className={`${colors.containerColor} ${colors.hoverBg} ${colors.textColor} ${showImage ? 'flex' : 'hidden'} h-12! font-normal items-center justify-center rounded-full cursor-pointer px-4`}
                      >
                        New Image
                      </Button>

                      <Button
                        onPress={handlePlayIcon}
                        className={`${colors.containerColor} ${colors.hoverBg} ${colors.textColor} ${showImage ? 'flex' : 'hidden'} h-12! font-normal items-center justify-center rounded-full cursor-pointer px-4`}
                      >
                        Show{' '}
                        {
                          interactionsMap[
                            currentRosFeature?.type as keyof typeof interactionsMap
                          ]?.name
                        }
                      </Button>

                      {showCorrectAnswerButton && (
                        <ShowAnswerButton
                          interactionId={currentRosFeature.id}
                          showAnswerMap={showAnswerMap}
                          setShowAnswerMap={setShowAnswerMap}
                        />
                      )}

                      {!isPDFSlide(currentRosFeature?.type) && !isSpinners(currentRosFeature?.type) && !isTrackInteractions(currentRosFeature?.type) && showTotalResponders && <RollingCounter number={totalResponders} layoutTheme={isBgLight ? 'light' : 'dark'} />}
                    </div>
                  </div>
                }
              </section>
              <PinnedTiles
                ref={pinnedCommentRef}
              // onClose={() => {
              //   pinnedCommentRef.current = null;
              // }}
              />
            </div>
          </div>
        </PresentationView>
      </div>
      <SummaryModal
        isOpen={isSummaryOpen}
        onClose={onSummaryClose}
        onOpenChange={onSummaryOpenChange}
        questionId={currentRosFeature?.id}
        streamId={streamId}
        type={currentRosFeature?.type}
      />
    </div>
  );
};

export default InteractionLayout;
